import React, { useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { Grid } from '@interstate/components/Grid';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import ManualTradeIn from '../manualTradeIn/ManualTradeIn.interstate';
import { TradeInFormsContainer, TradeInSeparator } from '../trade.interstate.style';
import { TradeInValuation } from '../valuation/TradeInValuation';
import type { LienholderFormData } from '../../../types';
import { TradeCheckboxes, CheckboxType } from '../TradeCheckboxes';
import { useSingleTradeInFormProvider } from './TradeInFormProvider';

export type TradeInFormProps = {
    isEditDisabled: boolean;
    lienholderData: LienholderFormData;
    setLienholderData: Dispatch<SetStateAction<LienholderFormData>>;
    isTradeInFormDirtyRef?: React.MutableRefObject<boolean>;
};

export const TradeInForm = ({ isEditDisabled, lienholderData, setLienholderData, isTradeInFormDirtyRef }: TradeInFormProps) => {
    const {
        tradeInIndex,
        softSaveLienHolderData: providerSoftSaveLienHolderData,
        tradeInFormErrors
    } = useSingleTradeInFormProvider();
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const gridColumnSize = isDealCentralMultiScenario ? 12 : 6;
    const theme = useInterstateTheme();

    const hasLienholderDetailsBundleErrors = useMemo(() => {
        const lienholderErrors = (tradeInFormErrors as any)?.lienholder;
        return Boolean(
            lienholderErrors?.name?.hasError ||
                lienholderErrors?.accountNumber?.hasError ||
                lienholderErrors?.streetAddress?.hasError ||
                lienholderErrors?.city?.hasError ||
                lienholderErrors?.state?.hasError ||
                lienholderErrors?.zip?.hasError
        );
    }, [tradeInFormErrors]);

    const enableApplyChangesButton = !hasLienholderDetailsBundleErrors;

    const isLocked = dealLockHooks.useIsDealLocked();

    const softSaveLienHolderData = () => {
        if (!enableApplyChangesButton) {
            if (isTradeInFormDirtyRef) {
                isTradeInFormDirtyRef.current = true;
            }
            return;
        }

        providerSoftSaveLienHolderData(lienholderData);
        if (isTradeInFormDirtyRef) {
            isTradeInFormDirtyRef.current = false;
        }
    };

    return (
        <TradeInFormsContainer theme={theme}>
            <ManualTradeIn key={`manual-${tradeInIndex}`} isEditDisabled={isLocked || isEditDisabled} />
            <TradeInValuation
                key={`valuation-${tradeInIndex}`}
                isEditDisabled={isEditDisabled}
                lienholderData={lienholderData}
                setLienholderData={setLienholderData}
                softSaveLienHolderData={softSaveLienHolderData}
                isTradeInFormDirtyRef={isTradeInFormDirtyRef}
            />
            <TradeInSeparator />

            <Grid container data-testid="multiple-trade-in-grid" spacing={1}>
                <Grid xs={12} md={gridColumnSize}>
                    <TradeCheckboxes type={CheckboxType.LEASE} isEditDisabled={isEditDisabled} />
                </Grid>
                <Grid xs={12} md={gridColumnSize}>
                    <TradeCheckboxes type={CheckboxType.BUYER_REGISTERED} tradeInputsDisabled={isEditDisabled} />
                </Grid>
                <Grid xs={12} md={gridColumnSize}>
                    <TradeCheckboxes type={CheckboxType.VEHICLE_REGISTERED} tradeInputsDisabled={isEditDisabled} />
                </Grid>
                <Grid xs={12} md={gridColumnSize}>
                    <TradeCheckboxes type={CheckboxType.INCLUDE_OFFER} />
                </Grid>
            </Grid>
        </TradeInFormsContainer>
    );
};
